import React, { useState, useEffect, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import {
  upsertOverrideCanonico,
  deleteOverrideCanonico,
  getOverrideCanonico,
} from "./api"
import { TIPO_DOCUMENTO_DIAN, formatFecha, origenConfig, formatNumber } from "./helpers"
import CopiarNit from "./CopiarNit"

/**
 * Wizard "Modo limpieza": drawer lateral que recorre uno por uno los terceros
 * con variantes detectadas, mostrando para cada uno el form de edición de
 * nombre canónico (override manual). Al guardar/eliminar avanza al siguiente.
 */
const WizardLimpieza = ({
  abierto, onCerrar, terceros, nitemisor, actualizadoPor, onCambioGuardado,
}) => {
  const [indice, setIndice] = useState(0)
  const [overrideExistente, setOverrideExistente] = useState(null)
  const [nombreCanonico, setNombreCanonico] = useState("")
  const [tipoDocumento, setTipoDocumento] = useState("31")
  const [notas, setNotas] = useState("")
  const [cargando, setCargando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [eliminando, setEliminando] = useState(false)
  const [error, setError] = useState("")
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [validados, setValidados] = useState(() => new Set())

  // Snapshot interno de la lista — se congela al abrir para que validar uno
  // dentro de la sesión NO lo saque del wizard de inmediato (sigue visible
  // con ✓). Al cerrar + reabrir, se toma una nueva snapshot del prop que ya
  // viene filtrado sin los validados.
  const [listaWizard, setListaWizard] = useState([])

  const total = listaWizard.length
  const tercero = listaWizard[indice]

  const cargarOverride = useCallback(async (t) => {
    if (!t) return
    setCargando(true)
    setError("")
    setConfirmDelete(false)
    setOverrideExistente(null)
    setNombreCanonico(t.nombre_canonico || t.nombrereceptor || "")
    setTipoDocumento("31")
    setNotas("")

    try {
      const res = await getOverrideCanonico(t.nitreceptor, nitemisor)
      const ov = res.data?.data
      if (ov) {
        setOverrideExistente(ov)
        setNombreCanonico(ov.nombre_canonico || "")
        setTipoDocumento(ov.tipo_documento || "31")
        setNotas(ov.notas || "")
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        setError(err.response?.data?.error || err.message || "No se pudo consultar el override.")
      }
    } finally {
      setCargando(false)
    }
  }, [nitemisor])

  // Reset + snapshot al abrir (NO se actualiza con cambios al prop durante la sesión)
  useEffect(() => {
    if (!abierto) return
    setIndice(0)
    setValidados(new Set())
    setListaWizard(terceros || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto])

  useEffect(() => {
    if (abierto && tercero) cargarOverride(tercero)
  }, [abierto, tercero, cargarOverride])

  // Atajos teclado: Esc cierra, ←/→ navega
  useEffect(() => {
    if (!abierto) return
    const handler = e => {
      if (e.key === "Escape") onCerrar()
      else if (e.key === "ArrowLeft" && indice > 0) setIndice(i => i - 1)
      else if (e.key === "ArrowRight" && indice < total - 1) setIndice(i => i + 1)
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [abierto, onCerrar, indice, total])

  // Progreso: cuántos ya tienen override manual + los que validamos en esta sesión
  // (debe ir ANTES del early return para no violar reglas de hooks)
  const yaManuales = useMemo(() => {
    return listaWizard.filter(t =>
      t.nombre_canonico_origen === "manual" || validados.has(t.nitreceptor),
    ).length
  }, [listaWizard, validados])

  if (!abierto || !tercero) return null

  const irAnterior = () => indice > 0 && setIndice(indice - 1)
  const irSiguiente = () => indice < total - 1 && setIndice(indice + 1)
  const irA = (i) => setIndice(i)

  const handleGuardar = async () => {
    setError("")
    if (!nombreCanonico.trim()) {
      setError("El nombre canónico no puede estar vacío.")
      return
    }
    setGuardando(true)
    try {
      await upsertOverrideCanonico({
        nitemisor,
        nitreceptor: tercero.nitreceptor,
        nombre_canonico: nombreCanonico.trim(),
        tipo_documento: tipoDocumento,
        actualizado_por: actualizadoPor,
        notas: notas.trim() || undefined,
      })
      setValidados(prev => new Set(prev).add(tercero.nitreceptor))
      onCambioGuardado?.()
      // Avanzar al siguiente automáticamente, o cerrar si era el último
      if (indice < total - 1) {
        setIndice(indice + 1)
      } else {
        onCerrar()
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al guardar.")
    } finally {
      setGuardando(false)
    }
  }

  const handleEliminar = async () => {
    setError("")
    setEliminando(true)
    try {
      await deleteOverrideCanonico({
        nitemisor,
        nitreceptor: tercero.nitreceptor,
        actualizado_por: actualizadoPor,
      })
      setValidados(prev => {
        const next = new Set(prev)
        next.delete(tercero.nitreceptor)
        return next
      })
      onCambioGuardado?.()
      // Recargar el tercero actual para reflejar el cambio
      cargarOverride(tercero)
      setConfirmDelete(false)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al eliminar.")
    } finally {
      setEliminando(false)
    }
  }

  const origen = origenConfig(tercero.nombre_canonico_origen)
  const variantes = tercero.nombre_variantes || []
  const totalVarFacturas = variantes.reduce((s, v) => s + (Number(v.facturas) || 0), 0)
  const cualquierAccion = guardando || eliminando

  const pctProgreso = total > 0 ? Math.round((yaManuales / total) * 100) : 0

  return (
    <>
      <div
        onClick={cualquierAccion ? null : onCerrar}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(15, 23, 42, 0.5)",
          zIndex: 1040,
          animation: "fadeIn 0.2s ease",
        }}
      />
      <div
        style={{
          position: "fixed", top: 0, right: 0,
          height: "100vh", width: "min(720px, 95vw)",
          background: "#f8fafc", zIndex: 1050,
          boxShadow: "-8px 0 32px rgba(15, 23, 42, 0.15)",
          display: "flex", flexDirection: "column",
          animation: "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <style>{`
          @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        `}</style>

        {/* Header con progreso */}
        <div style={{
          padding: "16px 24px",
          background: "linear-gradient(135deg, #0f172a, #1e3a5f)",
          color: "#fff",
        }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
                <i className="mdi mdi-broom me-1" />
                Modo limpieza
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>
                {indice + 1} de {total} terceros con variantes
              </div>
            </div>
            <button
              type="button"
              onClick={onCerrar}
              disabled={cualquierAccion}
              style={{
                background: "rgba(255,255,255,0.1)", border: "none",
                color: "#fff", width: 36, height: 36, borderRadius: 8,
                cursor: cualquierAccion ? "not-allowed" : "pointer",
                fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center",
              }}
              title="Cerrar (Esc)"
            >
              <i className="mdi mdi-close" />
            </button>
          </div>

          {/* Barra de progreso */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.15)", borderRadius: 999, overflow: "hidden" }}>
              <div style={{
                width: `${pctProgreso}%`, height: "100%",
                background: "linear-gradient(90deg, #10b981, #34d399)",
                transition: "width 0.3s ease",
              }} />
            </div>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", fontWeight: 600, minWidth: 80, textAlign: "right" }}>
              {yaManuales}/{total} validados
            </span>
          </div>
        </div>

        {/* Navegación */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "10px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={irAnterior}
            disabled={indice === 0 || cualquierAccion}
          >
            <i className="mdi mdi-chevron-left" /> Anterior
          </button>
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 280, fontSize: 12 }}
            value={indice}
            onChange={e => irA(Number(e.target.value))}
            disabled={cualquierAccion}
          >
            {listaWizard.map((t, i) => {
              const esManual = t.nombre_canonico_origen === "manual" || validados.has(t.nitreceptor)
              return (
                <option key={t.nitreceptor} value={i}>
                  {esManual ? "✓ " : "○ "}{i + 1}. {t.nombre_canonico || t.nombrereceptor || t.nitreceptor}
                </option>
              )
            })}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={irSiguiente}
            disabled={indice >= total - 1 || cualquierAccion}
          >
            Siguiente <i className="mdi mdi-chevron-right" />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {/* Info del tercero */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                  <CopiarNit nit={tercero.nitreceptor} />
                  <span style={{
                    background: origen.bg, color: origen.color,
                    padding: "1px 8px", borderRadius: 4,
                    fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                  }}>
                    <i className={`mdi mdi-${origen.icon} me-1`} />
                    {origen.label}
                  </span>
                </div>
                <div style={{ fontSize: 16, color: "#0f172a", fontWeight: 700 }}>
                  {tercero.nombre_canonico || tercero.nombrereceptor || "—"}
                </div>
              </div>
              <span style={{
                background: "#fef3c7", color: "#92400e",
                padding: "4px 12px", borderRadius: 999,
                fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
              }}>
                {variantes.length} variantes · {formatNumber(totalVarFacturas)} facts
              </span>
            </div>
          </div>

          {/* Loading inicial */}
          {cargando && (
            <div style={{ padding: 30, textAlign: "center", color: "#64748b" }}>
              <Spinner size="sm" /> Buscando override existente…
            </div>
          )}

          {!cargando && (
            <>
              {/* Banner */}
              {overrideExistente ? (
                <div style={{
                  background: "#dbeafe", border: "1px solid #bfdbfe",
                  color: "#1e40af", padding: "10px 14px", borderRadius: 8,
                  marginBottom: 16, fontSize: 13,
                }}>
                  <i className="mdi mdi-check-decagram me-1" />
                  Override <strong>manual</strong> ya existe. Registrado por <strong>{overrideExistente.actualizado_por || "—"}</strong>
                  {" "}el {formatFecha(overrideExistente.actualizado_at)}.
                </div>
              ) : (
                <div style={{
                  background: "#fef3c7", border: "1px solid #fde68a",
                  color: "#92400e", padding: "10px 14px", borderRadius: 8,
                  marginBottom: 16, fontSize: 13,
                }}>
                  <i className="mdi mdi-information-outline me-1" />
                  Sin override manual. Nombre actual del <strong>algoritmo automático</strong>.
                </div>
              )}

              {/* Variantes como sugerencias */}
              {variantes.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 8 }}>
                    <i className="mdi mdi-cursor-default-click-outline me-1" />
                    Variantes detectadas — clic para usar
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {variantes.map((v, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setNombreCanonico(v.nombre)}
                        style={{
                          background: nombreCanonico === v.nombre ? "#3b82f6" : "#fff",
                          color: nombreCanonico === v.nombre ? "#fff" : "#475569",
                          border: nombreCanonico === v.nombre ? "1px solid #3b82f6" : "1px solid #e2e8f0",
                          borderRadius: 6,
                          padding: "6px 12px", fontSize: 12, cursor: "pointer",
                          transition: "all 0.15s",
                          textAlign: "left",
                        }}
                        title={`Último uso: ${formatFecha(v.ultimo_uso)}`}
                      >
                        <strong>{v.nombre}</strong>
                        <span style={{ opacity: 0.7, marginLeft: 6, fontSize: 11 }}>· {v.facturas} facts</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Form */}
              <div className="mb-3">
                <label className="form-label small fw-semibold" style={{ color: "#334155" }}>
                  Nombre canónico <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={nombreCanonico}
                  onChange={e => setNombreCanonico(e.target.value)}
                  disabled={cualquierAccion}
                  placeholder="Ej. ALIMENTOS GARI S.A.S."
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold" style={{ color: "#334155" }}>
                    Tipo de documento
                  </label>
                  <select
                    className="form-select"
                    value={tipoDocumento}
                    onChange={e => setTipoDocumento(e.target.value)}
                    disabled={cualquierAccion}
                  >
                    {TIPO_DOCUMENTO_DIAN.map(t => (
                      <option key={t.value} value={t.value}>{t.value} · {t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold" style={{ color: "#334155" }}>
                    Notas <span style={{ color: "#94a3b8", fontWeight: 400 }}>(opcional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={notas}
                    onChange={e => setNotas(e.target.value)}
                    disabled={cualquierAccion}
                    placeholder="Ej. Nombre legal según RUT"
                  />
                </div>
              </div>

              {error && (
                <div style={{
                  background: "#fef2f2", border: "1px solid #fecaca",
                  color: "#991b1b", padding: "10px 14px", borderRadius: 8,
                  fontSize: 13, marginBottom: 12,
                }}>
                  <i className="mdi mdi-alert-circle me-1" />
                  {error}
                </div>
              )}

              {/* Confirmación de eliminar */}
              {confirmDelete && (
                <div style={{
                  background: "#fef2f2", border: "1px solid #fecaca",
                  color: "#991b1b", padding: "12px 16px", borderRadius: 8,
                  marginBottom: 12, fontSize: 13,
                }}>
                  <strong>¿Eliminar el override manual?</strong>
                  <div style={{ marginTop: 6 }}>
                    El tercero volverá a usar el algoritmo automático.
                  </div>
                  <div className="d-flex gap-2 mt-2">
                    <button type="button" className="btn btn-sm btn-danger" onClick={handleEliminar} disabled={eliminando}>
                      {eliminando ? <Spinner size="sm" /> : "Sí, eliminar"}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmDelete(false)} disabled={eliminando}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer con acciones */}
        <div style={{ background: "#fff", borderTop: "1px solid #e2e8f0", padding: "12px 24px", display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <div>
            {overrideExistente && !confirmDelete && !cargando && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => setConfirmDelete(true)}
                disabled={cualquierAccion}
              >
                <i className="mdi mdi-trash-can-outline me-1" />
                Eliminar override
              </button>
            )}
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={irSiguiente}
              disabled={indice >= total - 1 || cualquierAccion}
              title="Saltar sin guardar"
            >
              Saltar
              <i className="mdi mdi-skip-next ms-1" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleGuardar}
              disabled={cualquierAccion || cargando}
              title="Guardar y avanzar al siguiente"
            >
              {guardando ? (
                <><Spinner size="sm" className="me-1" /> Guardando…</>
              ) : (
                <>
                  <i className="mdi mdi-content-save me-1" />
                  Guardar y siguiente
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

WizardLimpieza.propTypes = {
  abierto: PropTypes.bool.isRequired,
  onCerrar: PropTypes.func.isRequired,
  terceros: PropTypes.array,
  nitemisor: PropTypes.string.isRequired,
  actualizadoPor: PropTypes.string,
  onCambioGuardado: PropTypes.func,
}

export default WizardLimpieza
